import { useEffect, useState } from "react";
import { ArrowRight, Check, Tag } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { gbp, inputCls } from "@/lib/booking";

export function PromoCode({ form, promo, setPromo, autoCode = "", onRemove }) {
  const [open, setOpen] = useState(Boolean(promo || autoCode));
  const [code, setCode] = useState(promo?.promo_code || autoCode);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const apply = async (value = code) => {
    if (!value.trim()) return setError("Please enter a promo code.");
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/promos/validate", {
        code: value, service_slug: form.service_slug, property_key: form.property_key,
        frequency: form.frequency || null, extras: form.extras, email: form.email || null,
      });
      setPromo(data);
    } catch (err) {
      setPromo(null);
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoCode && !promo) apply(autoCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = () => { setPromo(null); setCode(""); setError(""); onRemove?.(); };

  if (promo) {
    return (
      <div className="mt-6 flex flex-col gap-3 rounded-2xl border border-sage bg-sage-light px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6" data-testid="promo-applied">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sage-dark text-canvas"><Check size={15} /></span>
          <div>
            <p className="text-base font-medium text-ink">
              Promo code applied: <span className="font-mono text-sm tracking-[0.1em] text-blue" data-testid="promo-applied-code">{promo.promo_code}</span>
            </p>
            <p className="mt-1 text-sm text-body" data-testid="promo-saved">You saved {gbp(promo.discount_amount)} on your cleaning service</p>
          </div>
        </div>
        <button type="button" onClick={remove} data-testid="promo-remove-button"
          className="self-start rounded-full border border-sage-dark/40 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-ink transition-colors hover:border-blue hover:text-blue sm:self-auto">
          Remove
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} data-testid="promo-toggle"
        className="group mt-6 flex w-full items-center gap-4 rounded-2xl border border-sage bg-sage-light/70 px-5 py-5 text-left transition-colors hover:border-blue hover:bg-sage-light sm:px-6">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue text-canvas"><Tag size={17} /></span>
        <span className="flex-1">
          <span className="block font-serif text-2xl leading-tight text-ink">Have a promo code?</span>
          <span className="mt-1 block text-sm text-body">Enter your code and apply your discount &rarr;</span>
        </span>
        <ArrowRight size={18} className="hidden shrink-0 text-blue transition-transform group-hover:translate-x-1 sm:block" />
      </button>
    );
  }

  return (
    <div className="mt-6 rounded-2xl border border-sage bg-sage-light/70 px-5 py-5 sm:px-6" data-testid="promo-section">
      <label htmlFor="promo-input" className="block font-serif text-2xl leading-tight text-ink">Promo code</label>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <input id="promo-input" autoFocus className={`${inputCls} uppercase tracking-[0.08em]`} value={code} placeholder="Enter code" maxLength={30}
          onChange={(e) => { setCode(e.target.value); setError(""); }}
          onKeyDown={(e) => e.key === "Enter" && apply()} data-testid="promo-input" />
        <button type="button" onClick={() => apply()} disabled={loading} data-testid="promo-apply-button"
          className="shrink-0 rounded-xl bg-blue px-8 py-3.5 font-mono text-[11px] uppercase tracking-[0.15em] text-canvas transition-colors hover:bg-blue-hover disabled:opacity-60">
          {loading ? "Applying..." : "Apply"}
        </button>
      </div>
      {error && <p className="mt-3 text-sm text-red-700" data-testid="promo-error" aria-live="polite">{error}</p>}
      <p className="mt-3 text-xs text-faint">Promo codes apply to the cleaning service only. Extras are charged at their full price.</p>
    </div>
  );
}

export function PriceBreakdown({ q, promo }) {
  const Row = ({ label, value, testId, muted }) => (
    <div className={`flex items-baseline justify-between gap-4 py-2 text-sm ${muted ? "text-faint" : "text-ink"}`}>
      <span>{label}</span>
      <span data-testid={testId}>{value}</span>
    </div>
  );
  const off = promo.discount_type === "percent" ? `${promo.discount_value}% off` : `${gbp(promo.discount_value)} off`;
  return (
    <div className="mt-5 rounded-2xl border border-subtle px-5 py-3" data-testid="price-breakdown">
      <Row label={`${q.service.name} — ${q.property.label}`} value={gbp(promo.original_cleaning_price)} testId="breakdown-original" />
      <Row label={`Promo code ${promo.promo_code} (${off})`} value={`-${gbp(promo.discount_amount)}`} testId="breakdown-discount" muted />
      <Row label="Discounted cleaning price" value={gbp(promo.final_cleaning_price)} testId="breakdown-discounted" />
      {promo.extras.length > 0 && (
        <div className="mt-1 border-t border-subtle pt-1" data-testid="breakdown-extras">
          {promo.extras.map((e) => <Row key={e.key} label={e.name} value={gbp(e.price)} testId={`breakdown-extra-${e.key}`} />)}
        </div>
      )}
      <p className="border-t border-subtle pt-2 text-xs text-faint">Promo codes apply to the cleaning service only. Extras are charged at their full price.</p>
    </div>
  );
}
